import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, PageLoader } from '@lex/ui';
import { apiClient } from '../lib/api';
import { PAYMENTS_ENABLED } from '../lib/features';

export function CheckoutPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const planKey = params.get('plan') ?? '';
  const plans = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => apiClient.chat.plans(),
    enabled: PAYMENTS_ENABLED,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);

  const plan = plans.data?.find((item) => item.key === planKey);

  if (!PAYMENTS_ENABLED) {
    return (
      <div className="mx-auto max-w-xl rounded-3xl border border-line bg-white/90 p-8 text-center">
        <h1 className="text-[28px] font-semibold text-ink">Оплата пока недоступна</h1>
        <p className="mt-2 text-[14.5px] leading-6 text-muted">
          Мы подключаем эквайринг. Пока можно задать вопрос юристу в чате — формат и стоимость
          обсудим там.
        </p>
        <Link to="/chat" className="mt-5 inline-block text-sm font-semibold text-accent">
          Перейти в чат
        </Link>
      </div>
    );
  }

  if (plans.isLoading) return <PageLoader />;

  if (!plan) {
    return (
      <div className="mx-auto max-w-xl rounded-3xl border border-line bg-white/90 p-8 text-center">
        <p className="text-lg font-semibold text-ink">Тариф не выбран</p>
        <Link to="/tariffs" className="mt-4 inline-block text-sm font-semibold text-accent">
          Выбрать тариф
        </Link>
      </div>
    );
  }

  const pay = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const payment = await apiClient.chat.createPayment(plan.key);
      await apiClient.chat.confirmPayment(payment.id);
      setPaid(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось провести оплату');
    } finally {
      setBusy(false);
    }
  };

  if (paid) {
    return (
      <div className="mx-auto max-w-xl rounded-3xl border border-line bg-white/90 p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-soft text-2xl text-success">
          ✓
        </div>
        <h1 className="mt-4 text-[28px] font-semibold text-ink">Оплата прошла</h1>
        <p className="mt-2 text-[14.5px] leading-6 text-muted">
          Тариф «{plan.name}» активирован. Можно задать вопрос юристу.
        </p>
        <button
          type="button"
          onClick={() => navigate('/chat')}
          className="mt-5 rounded-xl bg-ink-gradient px-6 py-3 text-[15px] font-semibold text-white"
        >
          Перейти в чат
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-10">
      <Link to="/tariffs" className="text-[14px] font-semibold text-accent">
        ← К тарифам
      </Link>
      <h1 className="text-[30px] font-semibold leading-tight text-ink md:text-[40px]">Оплата</h1>
      <div className="rounded-3xl border border-line bg-white/90 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
              Тариф
            </div>
            <div className="mt-1 text-[22px] font-semibold text-ink">{plan.name}</div>
            <div className="mt-1 text-[13px] text-muted">{plan.description}</div>
          </div>
          <div className="text-right text-[22px] font-semibold text-ink">
            {plan.price.toLocaleString('ru-RU')} {plan.currency}
          </div>
        </div>
        <div className="mt-4 rounded-2xl bg-soft px-4 py-3 text-[13px] leading-5 text-muted">
          Это демо-оплата: платёж подтверждается сразу, реальные деньги не списываются. После
          подключения эквайринга здесь появится форма карты или СБП.
        </div>
        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
        <Button className="mt-5 w-full" size="lg" loading={busy} onClick={() => void pay()}>
          Оплатить {plan.price.toLocaleString('ru-RU')} {plan.currency}
        </Button>
      </div>
    </div>
  );
}
